/**
 * The renter's half of the enquiry form: two RPCs, no library, no session.
 *
 * The reasoning is `publicQuoteApi.js`'s, and it is not repeated here because it has not changed:
 * the Supabase client exists to hold a session the renter must never acquire, and it keys that
 * session by project URL, so a second client built in the tab beside the product could stamp on an
 * agent's own. This is `fetch`, one header set, and two POSTs.
 *
 * Everything it can reach is decided by migration 0030: `anon` holds EXECUTE on exactly these two
 * functions and SELECT on nothing at all.
 */

const rpc = async (fn, args, { url, anonKey, fetchImpl }) => {
  const res = await (fetchImpl ?? fetch)(`${String(url).replace(/\/+$/, '')}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      authorization: `Bearer ${anonKey}`,
      'content-type': 'application/json',
      'content-profile': 'mtr',
    },
    body: JSON.stringify(args),
  })
  if (!res.ok) {
    /* A failure here is OURS, and the page says so in words rather than printing a status code at
       somebody who cannot act on one. */
    const detail = await res.text().catch(() => '')
    const err = new Error(`the enquiry service answered ${res.status}`)
    err.detail = detail.slice(0, 400)
    throw err
  }
  return res.json()
}

export function createPublicEnquiryApi({ url, anonKey, fetchImpl = null }) {
  if (!url || !anonKey) throw new Error('the renter page needs the project url and its anon key')
  const cfg = { url, anonKey, fetchImpl }
  return {
    /** `{ ok: true, listingTitle, answered }` or `{ ok: false, reason }` — never an exception for "no". */
    open: (slug) => rpc('enquiry_for_link', { p_slug: slug }, cfg),

    /**
     * Her answers, onto the lead this link was made for.
     *
     * IT THROWS ON A REFUSAL, and that is deliberate: `InquiryForm` is built to survive a throw by
     * keeping what she typed and offering another go (mtr#85). Returning `{ ok: false }` quietly
     * would let the form thank her for an enquiry nobody received.
     */
    send: async (slug, submission) => {
      const r = await rpc('enquiry_from_link', { p_slug: slug, p_submission: submission }, cfg)
      if (!r?.ok) {
        const err = new Error(`the enquiry was not recorded: ${r?.reason ?? 'unknown'}`)
        err.reason = r?.reason ?? 'unknown'
        throw err
      }
      return r
    },
  }
}
